import { useState } from 'react';
import { IconBook2, IconClock, IconSearch } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import '../edu.i18n';
import { PageHeader, useLayout } from '@hana/shell';
import { Button, Skeleton, StateView } from '@hana/ui';
import { COURSES, type Course } from '../data/courses';
import { ctaOf, matches, pctLabel } from '../data/logic';
import { useEdu } from '../useEdu';
import { useCourseDrawer } from './CourseDrawer';
import s from '../edu.module.css';

// Màn Khoá học của tôi (board Mint-Training, app `mine`): bảng khoá được giao, tiến độ, hạn, nút học tiếp.

const Path = ({ d, size = 20 }: { d: string; size?: number }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);

export function SearchRow({ placeholder, q, onQ, sum }: { placeholder: string; q: string; onQ: (v: string) => void; sum: string }) {
  return (
    <div className={s.subRow}>
      <label className={`${s.card} ${s.search}`}>
        <IconSearch size={16} aria-hidden="true" />
        <input aria-label={placeholder} placeholder={placeholder} value={q} onChange={(e) => onQ(e.target.value)} />
      </label>
      <span className={s.sum}>{sum}</span>
    </div>
  );
}

const barColor = (c: Course) => (c.passed ? 'var(--ok-dot)' : c.late ? 'var(--d-amber)' : 'var(--d-blue)');

function CoursesBody({ q, open }: { q: string; open: (c: Course) => void }) {
  const t = useT();
  const list = COURSES.filter((c) => matches(c, q));
  return (
    <section className={`${s.card} ${s.tbl}`} aria-label="Khoá học được giao">
      <div className={`${s.trow} ${s.thead}`}><span /><span>{t('ed.cCourse')}</span><span>{t('ed.cContent')}</span><span>{t('ed.cProgress')}</span><span>{t('ed.cDue')}</span><span /></div>
      {list.map((c) => {
        const cta = ctaOf(c);
        const fin = c.done === c.total && !c.passed;
        return (
          <div key={c.id} className={s.trow}>
            <span className={s.cIc} data-ok={c.passed ? '1' : undefined}><Path d={c.icon} /></span>
            <div className={s.cName}>
              <button type="button" title={c.cat} onClick={() => open(c)}>{c.k}</button>
              {c.required && <span className={s.pill} data-tone="go" title="Bắt buộc theo ghế · sinh task TRN">TRN</span>}
            </div>
            <div className={s.cMeta} title={c.sub}>
              <span><IconBook2 size={14} aria-hidden="true" />{c.total}</span>
              <span><IconClock size={14} aria-hidden="true" />{c.minutes}'</span>
            </div>
            <div>
              <div className={s.pgTop}><span>{c.done}/{c.total}</span><b>{pctLabel(c)}</b></div>
              <div className={`${s.bar} ${s.bar8}`}><i style={{ width: pctLabel(c), background: barColor(c) }} /></div>
            </div>
            <div className={s.cDue} data-late={c.late ? '1' : undefined} title={c.dueSub}>{c.due}</div>
            <button type="button" className={s.cta} data-primary={fin ? '1' : undefined} onClick={() => open(c)}>{cta}</button>
          </div>
        );
      })}
      {!list.length && <div className={s.noHit}>Không có khoá nào khớp “{q}”</div>}
    </section>
  );
}

export function CoursesScreen() {
  const t = useT();
  const { state } = useEdu();
  const phone = useLayout() === 'phone';
  const cd = useCourseDrawer();
  const [q, setQ] = useState('');
  return (
    <>
      <PageHeader
        title="Khoá học của tôi"
        actions={state === 'ready' ? <Button variant="primary" onClick={() => cd.open(COURSES[0]!)}>{t('ed.continue')}</Button> : undefined}
      />
      <div className={s.page}>
        {state === 'ready' && !phone && <SearchRow placeholder="Tìm khoá học" q={q} onQ={setQ} sum={`${COURSES.length} khoá`} />}
      </div>
      <StateView
        state={state}
        title={state === 'empty' ? 'Chưa có khoá học nào được giao' : undefined}
        desc={state === 'empty' ? 'Chưa có khoá bắt buộc.' : undefined}
        skeleton={<div className={s.skel}><Skeleton rows={5} /></div>}
      >
        <div className={s.page}><CoursesBody q={q} open={cd.open} /></div>
      </StateView>
      {cd.node}
    </>
  );
}
